// Bodega — Transferencias entre pañoles (Bloque 4)
// Listado + crear + detalle con firma + descargar PDF.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Package, Plus, ArrowRight, Send, FileDown, XCircle,
  CheckCircle2, Truck, Loader2, QrCode, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const ESTADO_BADGE = {
  borrador: { color: 'bg-slate-200 text-slate-700', label: 'Borrador' },
  enviada: { color: 'bg-amber-100 text-amber-800 border border-amber-300', label: 'En tránsito' },
  recibida: { color: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Recibida' },
  anulada: { color: 'bg-rose-100 text-rose-800 border border-rose-300', label: 'Anulada' },
};

export default function TransferenciasPage() {
  const [tab, setTab] = useState('todas');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'todas') params.set('estado', tab);
      const res = await api.get(`/bodega/transferencias?${params}`);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Error cargando transferencias');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="transferencias-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Truck className="w-8 h-8 text-indigo-600" />
            Transferencias entre Pañoles
          </h1>
          <p className="text-slate-500 mt-1">
            Mueve herramientas entre pañoles con guía PDF + firma QR por ambos lados.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="btn-nueva-transferencia">
          <Plus className="w-4 h-4 mr-1" /> Nueva transferencia
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tabs-estado">
          <TabsTrigger value="todas">Todas</TabsTrigger>
          <TabsTrigger value="borrador">Borradores</TabsTrigger>
          <TabsTrigger value="enviada">En tránsito</TabsTrigger>
          <TabsTrigger value="recibida">Recibidas</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
            </div>
          ) : items.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-slate-500">
              Sin transferencias.
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {items.map((t) => {
                const badge = ESTADO_BADGE[t.estado] || ESTADO_BADGE.borrador;
                return (
                  <Card
                    key={t.id}
                    className="cursor-pointer hover:shadow-md transition"
                    onClick={() => setDetail(t)}
                    data-testid={`card-transfer-${t.id}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="text-sm">
                          <span className="font-semibold">{t.panol_origen_nombre}</span>
                          <ArrowRight className="inline w-4 h-4 mx-2 text-slate-400" />
                          <span className="font-semibold">{t.panol_destino_nombre}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {(t.items || []).length} htas
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{(t.iniciado_en || '').slice(0, 10)}</span>
                        <Badge className={badge.color}>{badge.label}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {showNew && <NuevaTransferenciaDialog
        open={showNew} onClose={() => setShowNew(false)} onCreated={(t) => {
          setShowNew(false); setDetail(t); load();
        }}
      />}

      {detail && <DetalleTransferenciaDialog
        open={!!detail} transfer={detail}
        onClose={() => { setDetail(null); load(); }}
      />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Dialog: Nueva Transferencia
// ═══════════════════════════════════════════════════════
function NuevaTransferenciaDialog({ open, onClose, onCreated }) {
  const [panoles, setPanoles] = useState([]);
  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [htasOrigen, setHtasOrigen] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [observaciones, setObservaciones] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/bodega/panoles?activos_solo=true');
        setPanoles(res.data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!origenId) { setHtasOrigen([]); setSelected(new Set()); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/bodega/panoles/${origenId}`);
        setHtasOrigen(res.data.herramientas || []);
        setSelected(new Set());
      } catch (err) {
        toast.error('Error cargando herramientas del pañol');
      } finally { setLoading(false); }
    })();
  }, [origenId]);

  const filteredHtas = htasOrigen.filter((h) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (h.codigo_material || '').toLowerCase().includes(q)
      || (h.descripcion || '').toLowerCase().includes(q)
      || (h.asignado_a_nombre || '').toLowerCase().includes(q);
  });

  const toggle = (codigo) => {
    const ns = new Set(selected);
    if (ns.has(codigo)) ns.delete(codigo); else ns.add(codigo);
    setSelected(ns);
  };

  const toggleAll = () => {
    if (selected.size === filteredHtas.length) setSelected(new Set());
    else setSelected(new Set(filteredHtas.map((h) => h.codigo_material)));
  };

  const crear = async () => {
    if (!origenId || !destinoId) { toast.error('Selecciona origen y destino'); return; }
    if (origenId === destinoId) { toast.error('Origen y destino deben ser distintos'); return; }
    if (selected.size === 0) { toast.error('Selecciona al menos una herramienta'); return; }
    setCreating(true);
    try {
      const res = await api.post('/bodega/transferencias/iniciar', {
        panol_origen_id: origenId,
        panol_destino_id: destinoId,
        codigos: Array.from(selected),
        observaciones,
      });
      toast.success(`Transferencia creada · ${res.data.transferencia.items.length} htas`);
      onCreated(res.data.transferencia);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error creando transferencia');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="dialog-nueva-transferencia">
        <DialogHeader><DialogTitle>Nueva Transferencia</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Pañol origen</label>
              <Select value={origenId} onValueChange={setOrigenId}>
                <SelectTrigger data-testid="select-origen"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {panoles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} ({p.total_herramientas})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Pañol destino</label>
              <Select value={destinoId} onValueChange={setDestinoId}>
                <SelectTrigger data-testid="select-destino"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {panoles.filter((p) => p.id !== origenId).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {origenId && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por código, descripción o persona..."
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  className="text-sm"
                  data-testid="input-search-htas"
                />
                <Button size="sm" variant="outline" onClick={toggleAll}>
                  {selected.size === filteredHtas.length && filteredHtas.length ? 'Quitar todas' : 'Marcar todas'}
                </Button>
              </div>
              <div className="border rounded-md max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-slate-500 text-sm">Cargando...</div>
                ) : filteredHtas.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {htasOrigen.length === 0 ? 'Este pañol no tiene herramientas asignadas' : 'Sin resultados'}
                  </div>
                ) : filteredHtas.map((h) => (
                  <label
                    key={h.codigo_material}
                    className={`flex items-center gap-3 p-2 border-b cursor-pointer hover:bg-slate-50 text-xs ${
                      selected.has(h.codigo_material) ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(h.codigo_material)}
                      onChange={() => toggle(h.codigo_material)}
                      data-testid={`chk-hta-${h.codigo_material}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-indigo-700">{h.codigo_material}</div>
                      <div className="text-slate-600 truncate">{h.descripcion}</div>
                    </div>
                    <span className="text-slate-400">{h.asignado_a_nombre || '—'}</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {selected.size} de {filteredHtas.length} seleccionadas
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500">Observaciones (opcional)</label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={2} placeholder="Ej: transferencia para faena Brisas del Sol..."
              data-testid="input-observaciones"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={crear} disabled={creating || selected.size === 0} data-testid="btn-crear-transferencia">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
            Crear transferencia ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
// Dialog: Detalle + Firmar envío
// ═══════════════════════════════════════════════════════
function DetalleTransferenciaDialog({ open, transfer, onClose }) {
  const [pin, setPin] = useState('');
  const [sending, setSending] = useState(false);
  const [t, setT] = useState(transfer);
  const badge = ESTADO_BADGE[t.estado] || ESTADO_BADGE.borrador;
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const r = await api.get(`/bodega/transferencias/${t.id}`);
      setT(r.data);
    } catch { /* ignore */ }
  };

  const enviar = async () => {
    if (pin.length < 4) { toast.error('PIN inválido'); return; }
    setSending(true);
    try {
      await api.post(`/bodega/transferencias/${t.id}/enviar`, { pin });
      toast.success('Transferencia enviada · firma origen registrada');
      setPin('');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al firmar');
    } finally { setSending(false); }
  };

  const descargarPDF = async () => {
    try {
      const res = await api.get(`/bodega/transferencias/${t.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `transferencia_${t.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Error descargando PDF');
    }
  };

  const anular = async () => {
    if (!window.confirm('¿Anular esta transferencia? Las herramientas no se moverán.')) return;
    try {
      await api.delete(`/bodega/transferencias/${t.id}`);
      toast.success('Transferencia anulada');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const recibirAhora = () => {
    onClose();
    navigate(`/bodega/transferencias/recibir?token=${t.qr_token}`);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-detalle-transferencia">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Transferencia {t.id.slice(0, 8).toUpperCase()}
            <Badge className={badge.color}>{badge.label}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border rounded-md p-3 flex items-center justify-center gap-3">
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">Origen</div>
              <div className="font-bold">{t.panol_origen_nombre}</div>
              {t.firma_origen && (
                <div className="text-[10px] text-emerald-600 mt-1">
                  ✓ {t.firma_origen.nombre}
                </div>
              )}
            </div>
            <ArrowRight className="w-6 h-6 text-slate-400" />
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">Destino</div>
              <div className="font-bold">{t.panol_destino_nombre}</div>
              {t.firma_destino && (
                <div className="text-[10px] text-emerald-600 mt-1">
                  ✓ {t.firma_destino.nombre}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="font-semibold mb-1">Herramientas ({(t.items || []).length})</div>
            <div className="border rounded max-h-48 overflow-y-auto">
              {(t.items || []).map((it, i) => (
                <div key={`${it.codigo}-${i}`} className="p-2 border-b text-xs flex justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-indigo-700">{it.codigo}</div>
                    <div className="text-slate-600 truncate">{it.descripcion}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {t.observaciones && (
            <div>
              <div className="text-xs text-slate-500">Observaciones</div>
              <div className="text-xs italic">{t.observaciones}</div>
            </div>
          )}

          {t.estado === 'borrador' && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 space-y-2">
              <div className="text-sm font-semibold text-indigo-900">Firma del responsable ORIGEN</div>
              <p className="text-xs text-slate-600">
                El responsable del pañol <b>{t.panol_origen_nombre}</b> debe firmar con su PIN para enviar la transferencia.
              </p>
              <div className="flex gap-2">
                <Input
                  type="password" inputMode="numeric" maxLength={8}
                  placeholder="PIN 4-8 dígitos"
                  value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  data-testid="input-pin-envio"
                />
                <Button onClick={enviar} disabled={sending || pin.length < 4} data-testid="btn-firmar-enviar">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ml-1">Firmar y Enviar</span>
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-wrap gap-2">
          {t.estado === 'enviada' && (
            <Button variant="default" onClick={recibirAhora} data-testid="btn-ir-recibir">
              <QrCode className="w-4 h-4 mr-1" /> Ir a recepción
            </Button>
          )}
          <Button variant="outline" onClick={descargarPDF} data-testid="btn-descargar-pdf">
            <FileDown className="w-4 h-4 mr-1" /> Descargar PDF
          </Button>
          {(t.estado === 'borrador' || t.estado === 'enviada') && (
            <Button variant="destructive" onClick={anular} data-testid="btn-anular">
              <XCircle className="w-4 h-4 mr-1" /> Anular
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
